import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../config/supabase';
import LoadingSpinner from '../components/common/LoadingSpinner';
import RevenueChart from '../components/dashboard/RevenueChart';
import ConversionFunnel from '../components/dashboard/ConversionFunnel';
import TeamPerformanceTable from '../components/dashboard/TeamPerformanceTable';
import TopDealsCard from '../components/dashboard/TopDealsCard';
import PipelineHealthCard from '../components/dashboard/PipelineHealthCard';
import { 
  LuTrendingUp, 
  LuTrendingDown, 
  LuDollarSign, 
  LuTarget, 
  LuUsers, 
  LuActivity, 
  LuCalendar
} from 'react-icons/lu';
import { FiClock } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const ExecutiveDashboard = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('month'); // month, quarter, year
  const [metrics, setMetrics] = useState({
    totalRevenue: 0,
    revenueGrowth: 0,
    activeDeals: 0,
    avgDealSize: 0,
    winRate: 0,
    totalOpportunities: 0,
    pipelineValue: 0,
    avgSalesCycle: 0
  });
  const [opportunities, setOpportunities] = useState([]);
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    if (userProfile?.role === 'Admin') {
      fetchExecutiveData();
    }
  }, [userProfile, timeRange]);

  const fetchExecutiveData = async () => {
    try {
      setLoading(true);
      
      // Fetch all opportunities with related data
      const { data: oppsData, error: oppsError } = await supabase
        .from('opportunities')
        .select(`
          *,
          accounts (id, name, industry),
          contacts (id, first_name, last_name),
          users (id, full_name),
          teams (id, name)
        `);
        
      if (oppsError) throw oppsError;

      // Fetch teams
      const { data: teamsData, error: teamsError } = await supabase
        .from('teams')
        .select('*');
        
      if (teamsError) throw teamsError;

      setOpportunities(oppsData || []);
      setTeams(teamsData || []);
      
      // Calculate metrics
      calculateMetrics(oppsData || []);
      setError('');
    } catch (err) {
      console.error('Error fetching executive data:', err);
      setError('Failed to load executive dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = (opps) => {
    const now = new Date();
    const filterDate = getFilterDate(now, timeRange);
    
    // Filter opportunities by time range
    const filteredOpps = opps.filter(opp => 
      new Date(opp.created_at) >= filterDate
    );

    // Calculate total revenue (Closed Won)
    const wonDeals = filteredOpps.filter(opp => opp.stage === 'Closed Won');
    const totalRevenue = wonDeals.reduce((sum, opp) => sum + (parseFloat(opp.amount) || 0), 0);
    
    // Calculate active deals (not closed)
    const activeDeals = filteredOpps.filter(
      opp => !['Closed Won', 'Closed Lost'].includes(opp.stage)
    );
    
    // Calculate pipeline value
    const pipelineValue = activeDeals.reduce((sum, opp) => sum + (parseFloat(opp.amount) || 0), 0);

    // Calculate average deal size
    const avgDealSize = wonDeals.length > 0 ? totalRevenue / wonDeals.length : 0;

    // Calculate win rate
    const closedDeals = filteredOpps.filter(
      opp => ['Closed Won', 'Closed Lost'].includes(opp.stage)
    );
    const winRate = closedDeals.length > 0 
      ? (wonDeals.length / closedDeals.length) * 100 
      : 0;

    // Calculate revenue growth (compare with previous period)
    const previousPeriodDate = getFilterDate(filterDate, timeRange);
    const previousOpps = opps.filter(opp => {
      const oppDate = new Date(opp.created_at);
      return oppDate >= previousPeriodDate && oppDate < filterDate;
    });
    const previousRevenue = previousOpps
      .filter(opp => opp.stage === 'Closed Won')
      .reduce((sum, opp) => sum + (parseFloat(opp.amount) || 0), 0);
      
    const revenueGrowth = previousRevenue > 0 
      ? ((totalRevenue - previousRevenue) / previousRevenue) * 100 
      : 0;

    // Calculate average sales cycle (in days)
    const closedDealsWithDates = wonDeals.filter(
      opp => opp.created_at && opp.updated_at
    );
    const avgSalesCycle = closedDealsWithDates.length > 0
      ? closedDealsWithDates.reduce((sum, opp) => {
          const created = new Date(opp.created_at);
          const updated = new Date(opp.updated_at);
          const days = Math.floor((updated - created) / (1000 * 60 * 60 * 24));
          return sum + days;
        }, 0) / closedDealsWithDates.length
      : 0;

    setMetrics({
      totalRevenue,
      revenueGrowth,
      activeDeals: activeDeals.length,
      avgDealSize,
      winRate,
      totalOpportunities: filteredOpps.length,
      pipelineValue,
      avgSalesCycle
    });
  };

  const getFilterDate = (fromDate, range) => {
    const date = new Date(fromDate);
    switch (range) {
      case 'month':
        date.setMonth(date.getMonth() - 1);
        break;
      case 'quarter':
        date.setMonth(date.getMonth() - 3);
        break;
      case 'year':
        date.setFullYear(date.getFullYear() - 1);
        break;
      default:
        date.setMonth(date.getMonth() - 1);
    }
    return date;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  if (!userProfile) {
    return <LoadingSpinner size="large" />;
  }

  if (userProfile.role !== 'Admin') {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4">
        <p className="text-red-700">
          Access Denied: This dashboard is for executives and sales managers only.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Executive Dashboard</h2>
          <p className="mt-1 text-sm text-gray-600">
            Real-time insights for business development and sales performance
          </p>
        </div>
        
        {/* Time Range Filter */}
        <div className="flex items-center space-x-2">
          <SafeIcon icon={LuCalendar} className="h-5 w-5 text-gray-400" />
          <select 
            value={timeRange} 
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="month">Last Month</option>
            <option value="quarter">Last Quarter</option>
            <option value="year">Last Year</option>
          </select>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Revenue */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {formatCurrency(metrics.totalRevenue)}
              </p>
              <div className="flex items-center mt-2">
                {metrics.revenueGrowth >= 0 ? (
                  <>
                    <SafeIcon icon={LuTrendingUp} className="h-4 w-4 text-green-600 mr-1" />
                    <span className="text-sm font-medium text-green-600">
                      {formatPercentage(metrics.revenueGrowth)}
                    </span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={LuTrendingDown} className="h-4 w-4 text-red-600 mr-1" />
                    <span className="text-sm font-medium text-red-600">
                      {formatPercentage(metrics.revenueGrowth)}
                    </span>
                  </>
                )}
                <span className="text-xs text-gray-500 ml-2">vs previous period</span>
              </div>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={LuDollarSign} className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Pipeline Value */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Pipeline Value</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {formatCurrency(metrics.pipelineValue)}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                {metrics.activeDeals} active deals
              </p>
            </div>
            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={LuTarget} className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Win Rate */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Win Rate</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {metrics.winRate.toFixed(1)}%
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Avg deal: {formatCurrency(metrics.avgDealSize)}
              </p>
            </div>
            <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={LuActivity} className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Sales Cycle */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Avg Sales Cycle</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {Math.round(metrics.avgSalesCycle)} days
              </p>
              <p className="text-sm text-gray-500 mt-2">
                {metrics.totalOpportunities} total opportunities
              </p>
            </div>
            <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiClock} className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <RevenueChart opportunities={opportunities} timeRange={timeRange} />
        
        {/* Conversion Funnel */}
        <ConversionFunnel opportunities={opportunities} />
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Deals */}
        <TopDealsCard opportunities={opportunities} />
        
        {/* Pipeline Health */}
        <PipelineHealthCard opportunities={opportunities} />
        
        {/* Team Performance Table */}
        <div className="lg:col-span-1">
          <TeamPerformanceTable opportunities={opportunities} teams={teams} />
        </div>
      </div>
    </div>
  );
};

export default ExecutiveDashboard;